import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, MapPin, User, Mail, Lock, Phone, CheckCircle, UserPlus, ArrowRight } from 'lucide-react';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const Register = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedTier, setSelectedTier] = useState('starter');
  const [formData, setFormData] = useState({
  email: '',
  password: '',
  confirmPassword: '',
  name: '',
  organizationName: '',
  phone: '',
  company: '',
  street: '',
  city: '',
  postal_code: '',
  country: 'Switzerland'
});

  const getApiUrl = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:3001/api';
    }
    return 'https://app.alpenscreen.ch/api';
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const isPasswordValid = () => {
    const { password } = formData;
    if (password.length < 12) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/[0-9]/.test(password)) return false;
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'`~;]/.test(password)) return false;
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // ✅ Password Confirmation Check (VOR allem anderen)
    if (formData.password !== formData.confirmPassword) {
      setError(t('register.error.passwordMismatch'));
      return;
    }

    // ✅ Password Complexity Check
    if (!isPasswordValid()) {
      setError(t('register.error.passwordWeak'));
      return;
    }

    // ZIP Validation
    if (!/^\d{4}$/.test(formData.zip)) {
      setError(t('register.error.zipFormat'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const API_URL = getApiUrl();
      
      // ✅ confirmPassword rausfiltern bevor wir es ans Backend schicken
      const { confirmPassword, ...dataToSend } = formData;
      
      const response = await fetch(`${API_URL}/auth/register-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...dataToSend,  // ← OHNE confirmPassword
          tier: selectedTier
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('register.error.failed'));
      }

      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1E7FCC] via-[#1B6DB8] to-[#185CA4] flex items-center justify-center p-4 md:p-8">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl p-8 md:p-12">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('register.success.title')}
            </h2>
            <p className="text-lg text-gray-600 mb-3">
              {t('register.success.emailSent')} <strong className="text-[#1E7FCC]">{formData.email}</strong>
            </p>
            <p className="text-base text-gray-600 mb-8 leading-relaxed">
              {t('register.success.instructions')}
            </p>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-8 flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-left text-sm text-blue-800">
                <span className="font-semibold">{t('register.success.tip.label')}</span> {t('register.success.tip.text')}
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gradient-to-r from-[#1E7FCC] to-[#185CA4] text-white rounded-xl font-semibold hover:from-[#1B6DB8] hover:to-[#164E91] transition-all shadow-lg hover:shadow-xl"
            >
              {t('register.success.backButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E7FCC] via-[#1B6DB8] to-[#185CA4] flex items-center justify-center p-4 md:p-8">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl overflow-hidden">
        <div className="grid md:grid-cols-[45%_55%] gap-0">
          
          {/* Left Side - Branding & Benefits */}
          <div className="bg-gradient-to-br from-[#1E7FCC] to-[#185CA4] p-6 md:p-10 text-white relative overflow-hidden flex flex-col justify-center">
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="none" fill="none">
                <path d="M0 300 L100 200 L200 250 L300 150 L400 200 L400 400 L0 400 Z" fill="white" opacity="0.3"/>
                <path d="M0 350 L150 250 L250 280 L400 220 L400 400 L0 400 Z" fill="white" opacity="0.2"/>
              </svg>
            </div>
            
            <div className="relative z-10">
              <div className="mb-8">
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  {t('register.sidebar.title')}
                </h1>
                <p className="text-blue-50 text-xl md:text-2xl leading-relaxed font-light mb-2">
                  {t('register.sidebar.subtitle')}
                </p>
              </div>

              {/* Pricing Plans */}
              <div className="mb-6">
                <h3 className="text-white font-semibold text-lg mb-4 text-center">{t('register.sidebar.choosePlan')}</h3>
                <div className="space-y-3">
                  {/* Starter Plan */}
                  <button
                    type="button"
                    onClick={() => setSelectedTier('starter')}
                    className={`
                      w-full backdrop-blur-sm rounded-xl p-5 relative transition-all cursor-pointer text-left
                      ${selectedTier === 'starter'
                        ? 'bg-white/20 border-2 border-white/40 shadow-lg'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                      }
                    `}
                  >
                    {selectedTier === 'starter' && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md">
                          <svg className="w-5 h-5 text-[#1E7FCC]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-white font-bold text-2xl">{t('register.pricing.starter.name')}</h4>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-white">{t('register.pricing.starter.price')}</div>
                        <div className="text-blue-100 text-sm">{t('register.pricing.starter.period')}</div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <p className="text-sm text-blue-100 leading-relaxed">{t('register.pricing.starter.trial')}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.starter.features.screens')}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.starter.features.basicFeatures')}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.starter.features.support')}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.starter.features.marketplace')}</span>
                      </div>
                    </div>
                  </button>

                  {/* Business Plan */}
                  <button
                    type="button"
                    onClick={() => setSelectedTier('business')}
                    className={`
                      w-full backdrop-blur-sm rounded-xl p-5 relative transition-all cursor-pointer text-left
                      ${selectedTier === 'business'
                        ? 'bg-white/20 border-2 border-white/40 shadow-lg'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                      }
                    `}
                  >
                    <div className="absolute -top-3 right-3">
                      <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                        {t('register.pricing.business.badge')}
                      </span>
                    </div>
                    {selectedTier === 'business' && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <div className="bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md">
                          <svg className="w-5 h-5 text-[#1E7FCC]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-white font-bold text-2xl">{t('register.pricing.business.name')}</h4>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-white">{t('register.pricing.business.price')}</div>
                        <div className="text-blue-100 text-sm">{t('register.pricing.business.period')}</div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <p className="text-sm text-blue-100 leading-relaxed">{t('register.pricing.business.trial')}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-x-2 gap-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.business.features.screens')}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.business.features.businessFeatures')}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.business.features.support')}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.business.features.marketplace')}</span>
                      </div>
                      <div className="flex items-start gap-2 col-span-2">
                        <div className="w-5 h-5 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-white">{t('register.pricing.business.features.analytics')}</span>
                      </div>
                    </div>
                  </button>
                </div>
                <div className="mt-3 flex items-center justify-center gap-2 text-blue-100 text-xs">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{t('register.pricing.footer.upgrade')}</span>
                </div>
                <p className="text-center text-blue-100 text-xs mt-2">{t('register.pricing.footer.currency')}</p>
              </div>
            </div>
          </div>

          {/* Right Side - Registration Form */}
          <div className="pt-2 pb-3 px-6 md:pt-2 md:pb-4 md:px-10 flex flex-col bg-white relative max-h-screen overflow-y-auto">
            {/* Logo */}
            <div 
              className="mb-1.5 text-center cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img 
                src="/assets/logo/alpenscreen-ultra-transparent-512px.png" 
                alt="AlpenScreen Logo" 
                className="w-44 md:w-56 h-auto mx-auto"
              />
            </div>

            <div className="flex-1">
              <form onSubmit={handleRegister} className="space-y-3">
                {/* Icon + Title side by side */}
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-xl shadow-md flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-[#1E7FCC]" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 leading-tight">
                      {t('register.form.title')}
                    </h2>
                    <p className="text-gray-600 text-xs leading-tight">
                      {t('register.form.subtitle')}
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-2.5 animate-slide-in">
                    <div className="flex items-start">
                      <svg className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="ml-2 text-xs font-medium text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                {/* Firmenname */}
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    {t('register.form.companyName')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Building2 size={16} />
                    </div>
                    <input
                      type="text"
                      name="orgName"
                      value={formData.orgName}
                      onChange={handleChange}
                      required
                      className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.companyNamePlaceholder')}
                    />
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    {t('register.form.name')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <User size={16} />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.namePlaceholder')}
                    />
                  </div>
                </div>

                {/* Strasse */}
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    {t('register.form.street')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <MapPin size={16} />
                    </div>
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      required
                      className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.streetPlaceholder')}
                    />
                  </div>
                </div>

                {/* PLZ & Stadt */}
                <div className="grid grid-cols-5 gap-2.5">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold mb-1 text-gray-700">
                      {t('register.form.zip')}
                    </label>
                    <input
                      type="text"
                      name="zip"
                      value={formData.zip}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{4}"
                      maxLength="4"
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.zipPlaceholder')}
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-xs font-semibold mb-1 text-gray-700">
                      {t('register.form.city')}
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.cityPlaceholder')}
                    />
                  </div>
                </div>

                {/* Telefon */}
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    {t('register.form.phone')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Phone size={16} />
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.phonePlaceholder')}
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5 ml-9">{t('register.form.phoneHelper')}</p>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    {t('register.form.email')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <Mail size={16} />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.emailPlaceholder')}
                      autoComplete="email"
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-0.5 ml-9">{t('register.form.emailHelper')}</p>
                </div>

                {/* ✨ Password with Strength Meter */}
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    {t('register.form.password')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">
                      <Lock size={16} />
                    </div>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full pl-9 pr-3 py-2.5 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:border-[#1E7FCC] transition-all"
                      placeholder={t('register.form.passwordPlaceholder')}
                      autoComplete="new-password"
                    />
                  </div>
                  
                  {/* ✨ Password Strength Meter */}
                  <PasswordStrengthMeter password={formData.password} />
                </div>

                {/* ✨ NEW: Confirm Password */}
                <div>
                  <label className="block text-xs font-semibold mb-1 text-gray-700">
                    {t('register.form.confirmPassword')}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">
                      <Lock size={16} />
                    </div>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      className={`w-full pl-9 pr-3 py-2.5 border-2 rounded-xl text-sm focus:outline-none focus:ring-4 transition-all ${
                        formData.confirmPassword && formData.password !== formData.confirmPassword
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500/30'
                          : 'border-gray-300 focus:border-[#1E7FCC] focus:ring-blue-500/30'
                      }`}
                      placeholder={t('register.form.confirmPasswordPlaceholder')}
                      autoComplete="new-password"
                    />
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-xs text-red-600 mt-1 ml-1 font-medium">
                      {t('register.password.mismatch')}
                    </p>
                  )}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && (
                    <p className="text-xs text-green-600 mt-1 ml-1 font-medium flex items-center gap-1">
                      <CheckCircle size={12} />
                      {t('register.password.match')}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !isPasswordValid() || formData.password !== formData.confirmPassword}
                  className={`
                    w-full py-3 px-5 rounded-xl font-bold text-sm
                    transition-all duration-200 transform
                    focus:outline-none focus:ring-4 focus:ring-blue-500/50
                    flex items-center justify-center gap-2
                    ${loading || !isPasswordValid() || formData.password !== formData.confirmPassword
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#1E7FCC] to-[#185CA4] text-white hover:from-[#1B6DB8] hover:to-[#164E91] hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0'
                    }
                  `}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>{t('register.form.submitting')}</span>
                    </>
                  ) : (
                    <>
                      <UserPlus size={16} />
                      <span>{t('register.form.submit')}</span>
                    </>
                  )}
                </button>

                <div className="text-center pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-600">
                    {t('register.footer.hasAccount')}{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="font-semibold text-[#1E7FCC] hover:text-[#1B6DB8] hover:underline transition-colors inline-flex items-center gap-1"
                    >
                      {t('register.footer.loginLink')}
                      <ArrowRight size={12} />
                    </button>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Register;